import { createContext, useContext, useState, useCallback, useRef, ReactNode } from "react";
import type {
  TheirStackJob,
  TheirStackAssignment,
  TheirStackTechManager,
  AssignmentStatus,
  AssignmentHistoryEntry,
  PagedResult,
  BulkAssignResult,
} from "../api/TheirStackJobsApi";
import {
  fetchTheirStackJobs,
  fetchAssignments,
  fetchAssignmentStatusCounts,
  fetchAssignmentDetail,
  fetchAssignmentHistory,
  updateAssignmentStatus,
  fetchTechManagers,
  updateTechTags,
  assignJob,
  unassignJob,
  reassignAssignment,
  activateJob,
  deactivateJob,
} from "../api/TheirStackJobsApi";

interface TheirStackJobsState {
  isAdmin: boolean;

  // Jobs (admin, "All TMs")
  jobs: PagedResult<TheirStackJob> | null;
  jobsLoading: boolean;
  jobFilters: {
    search: string;
    seniority: string;
    employment_type: string;
    active_only: boolean;
    unassigned_only: boolean;
    company_can_apply_only: boolean;
    page: number;
  };
  setJobFilters: (f: Partial<TheirStackJobsState["jobFilters"]>) => void;
  loadJobs: () => void;

  // Assignments tab / employee
  assignments: PagedResult<TheirStackAssignment> | null;
  assignmentsLoading: boolean;
  assignmentFilters: {
    search: string;
    seniority: string;
    employment_type: string;
    status_filter: string;
    hide_archived: boolean;
    page: number;
    employee_id: number | undefined;
    company_can_apply_only: boolean;
  };
  setAssignmentFilters: (f: Partial<TheirStackJobsState["assignmentFilters"]>) => void;
  loadAssignments: () => void;

  statusCounts: Record<string, number>;
  loadStatusCounts: () => void;

  // TM filter (admin assignments tab)
  techManagers: TheirStackTechManager[];
  techManagersLoaded: boolean;
  loadTechManagers: () => void;

  // Selected job (admin Jobs tab)
  selectedJob: TheirStackJob | null;
  openJob: (job: TheirStackJob) => void;
  closeJob: () => void;
  handleActivateJob: (id: number, active: boolean) => Promise<void>;
  handleAssignJob: (jobId: number, employeeIds: number[]) => Promise<BulkAssignResult>;
  handleUnassignJob: (jobId: number, employeeId: number) => Promise<void>;
  handleReassignAssignment: (assignmentId: number, employeeId: number) => Promise<void>;

  // Selected assignment detail
  selectedAssignment: TheirStackAssignment | null;
  assignmentDetailLoading: boolean;
  openAssignment: (assignment: TheirStackAssignment) => void;
  closeAssignment: () => void;
  updateAssignmentEnhancement: (assignmentId: number, patch: Partial<TheirStackAssignment>) => void;

  // Assignment history
  assignmentHistory: AssignmentHistoryEntry[];
  historyLoading: boolean;
  loadAssignmentHistory: (assignmentId: number) => void;

  // Status update
  handleStatusUpdate: (
    assignmentId: number,
    status: AssignmentStatus,
    notes: string | null,
  ) => Promise<void>;

  // Tech tags modal
  editingTechManager: TheirStackTechManager | null;
  openTechTagsModal: (tm: TheirStackTechManager) => void;
  closeTechTagsModal: () => void;
  handleUpdateTechTags: (employeeCode: string, tags: string[]) => Promise<void>;
}

const TheirStackJobsContext = createContext<TheirStackJobsState | null>(null);

export function useTheirStackJobs(): TheirStackJobsState {
  const ctx = useContext(TheirStackJobsContext);
  if (!ctx) throw new Error("useTheirStackJobs must be used within TheirStackJobsProvider");
  return ctx;
}

interface Props {
  isAdmin: boolean;
  children: ReactNode;
}

export function TheirStackJobsProvider({ isAdmin, children }: Props) {
  // Jobs
  const [jobs, setJobs] = useState<PagedResult<TheirStackJob> | null>(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobFilters, setJobFiltersState] = useState({
    search: "",
    seniority: "",
    employment_type: "",
    active_only: false,
    unassigned_only: false,
    company_can_apply_only: false,
    page: 1,
  });

  const setJobFilters = useCallback((f: Partial<typeof jobFilters>) => {
    setJobFiltersState((prev) => ({ ...prev, ...f }));
  }, []);

  const jobFiltersRef = useRef(jobFilters);
  jobFiltersRef.current = jobFilters;

  const loadJobs = useCallback(async () => {
    if (!isAdmin) return;
    setJobsLoading(true);
    try {
      const f = jobFiltersRef.current;
      const result = await fetchTheirStackJobs({
        page: f.page,
        page_size: 20,
        search: f.search || undefined,
        seniority: f.seniority || undefined,
        employment_type: f.employment_type || undefined,
        active_only: f.active_only || undefined,
        unassigned_only: f.unassigned_only || undefined,
        company_can_apply_only: f.company_can_apply_only || undefined,
      });
      setJobs(result);
    } catch {
      // silent
    } finally {
      setJobsLoading(false);
    }
  }, [isAdmin]);

  // Assignments
  const [assignments, setAssignments] = useState<PagedResult<TheirStackAssignment> | null>(null);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignmentFilters, setAssignmentFiltersState] = useState({
    search: "",
    seniority: "",
    employment_type: "",
    status_filter: "new",
    hide_archived: true,
    page: 1,
    employee_id: undefined as number | undefined,
    company_can_apply_only: false,
  });

  const setAssignmentFilters = useCallback((f: Partial<typeof assignmentFilters>) => {
    setAssignmentFiltersState((prev) => ({ ...prev, ...f }));
  }, []);

  const assignmentFiltersRef = useRef(assignmentFilters);
  assignmentFiltersRef.current = assignmentFilters;

  const loadAssignments = useCallback(async () => {
    setAssignmentsLoading(true);
    try {
      const f = assignmentFiltersRef.current;
      const result = await fetchAssignments(
        {
          page: f.page,
          page_size: 20,
          search: f.search || undefined,
          seniority: f.seniority || undefined,
          employment_type: f.employment_type || undefined,
          status_filter: f.status_filter || undefined,
          hide_archived: f.hide_archived,
          employee_id: f.employee_id,
          company_can_apply_only: f.company_can_apply_only || undefined,
        },
        isAdmin,
      );
      setAssignments(result);
    } catch {
      // silent
    } finally {
      setAssignmentsLoading(false);
    }
  }, [isAdmin]);

  // Status counts
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const loadStatusCounts = useCallback(async () => {
    try {
      const counts = await fetchAssignmentStatusCounts(
        isAdmin,
        isAdmin ? assignmentFiltersRef.current.employee_id : undefined,
      );
      setStatusCounts(counts);
    } catch {
      // silent
    }
  }, [isAdmin]);

  // Tech managers
  const [techManagers, setTechManagers] = useState<TheirStackTechManager[]>([]);
  const [techManagersLoaded, setTechManagersLoaded] = useState(false);
  const loadTechManagers = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const tms = await fetchTechManagers();
      setTechManagers(tms);
      setTechManagersLoaded(true);
    } catch {
      setTechManagersLoaded(true);
    }
  }, [isAdmin]);

  // Selected job (admin)
  const [selectedJob, setSelectedJob] = useState<TheirStackJob | null>(null);
  const openJob = useCallback((job: TheirStackJob) => setSelectedJob(job), []);
  const closeJob = useCallback(() => setSelectedJob(null), []);

  const handleActivateJob = useCallback(async (id: number, active: boolean) => {
    const fn = active ? activateJob : deactivateJob;
    const updated = await fn(id);
    setJobs((prev) =>
      prev ? { ...prev, items: prev.items.map((j) => (j.id === id ? updated : j)) } : prev,
    );
    setSelectedJob((prev) => (prev?.id === id ? updated : prev));
  }, []);

  const handleAssignJob = useCallback(async (jobId: number, employeeIds: number[]) => {
    const result = await assignJob(jobId, employeeIds);
    // Refresh jobs so unassigned_only filter stays consistent
    await loadJobs();
    return result;
  }, [loadJobs]);

  const handleUnassignJob = useCallback(async (jobId: number, employeeId: number) => {
    await unassignJob(jobId, employeeId);
    // Called from the Jobs ("All TMs") view's assignee list — refresh jobs so
    // unassigned_only filter stays consistent, matching handleAssignJob.
    await loadJobs();
  }, [loadJobs]);

  const handleReassignAssignment = useCallback(async (assignmentId: number, employeeId: number) => {
    const updated = await reassignAssignment(assignmentId, employeeId);
    setAssignments((prev) =>
      prev
        ? { ...prev, items: prev.items.map((a) => a.assignment_id === assignmentId ? { ...a, employee_id: updated.employee_id, assigned_by: updated.assigned_by } : a) }
        : prev,
    );
    setSelectedAssignment((prev) =>
      prev?.assignment_id === assignmentId ? { ...prev, employee_id: updated.employee_id, assigned_by: updated.assigned_by } : prev,
    );
  }, []);

  // Selected assignment detail
  const [selectedAssignment, setSelectedAssignment] = useState<TheirStackAssignment | null>(null);
  const [assignmentDetailLoading, setAssignmentDetailLoading] = useState(false);
  const assignmentDetailRequestId = useRef(0);

  // Opens the modal immediately with the (slim) list-item data already on hand —
  // no spinner on the card grid — then fills in the full detail (description,
  // enhanced_cv_data, etc.) in the background once it arrives.
  const openAssignment = useCallback(async (assignment: TheirStackAssignment) => {
    const requestId = ++assignmentDetailRequestId.current;
    setSelectedAssignment(assignment);
    setAssignmentDetailLoading(true);
    try {
      const detail = await fetchAssignmentDetail(assignment.assignment_id, isAdmin);
      if (requestId !== assignmentDetailRequestId.current) return; // superseded by a newer open/close
      setSelectedAssignment(detail);
    } catch {
      // Keep showing the summary data already displayed; description area
      // falls back to "unavailable" once loading stops.
    } finally {
      if (requestId === assignmentDetailRequestId.current) setAssignmentDetailLoading(false);
    }
  }, [isAdmin]);

  const closeAssignment = useCallback(() => {
    assignmentDetailRequestId.current++; // invalidate any in-flight detail fetch
    setSelectedAssignment(null);
    setAssignmentDetailLoading(false);
    setAssignmentHistory([]);
  }, []);

  // Patches the assignment's CV-enhancement fields in place (list + open modal)
  // after a successful enhance — avoids a full list reload for a change we
  // already know the shape of.
  const updateAssignmentEnhancement = useCallback(
    (assignmentId: number, patch: Partial<TheirStackAssignment>) => {
      setAssignments((prev) =>
        prev
          ? { ...prev, items: prev.items.map((a) => (a.assignment_id === assignmentId ? { ...a, ...patch } : a)) }
          : prev,
      );
      setSelectedAssignment((prev) =>
        prev?.assignment_id === assignmentId ? { ...prev, ...patch } : prev,
      );
    },
    [],
  );

  // Assignment history
  const [assignmentHistory, setAssignmentHistory] = useState<AssignmentHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadAssignmentHistory = useCallback(async (assignmentId: number) => {
    setHistoryLoading(true);
    try {
      const history = await fetchAssignmentHistory(assignmentId, isAdmin);
      setAssignmentHistory(history);
    } catch {
      setAssignmentHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [isAdmin]);

  // Status update
  const handleStatusUpdate = useCallback(
    async (assignmentId: number, status: AssignmentStatus, notes: string | null) => {
      await updateAssignmentStatus(assignmentId, status, notes, isAdmin);

      // A status change can move the item outside the currently active filter
      // (e.g. default filter is "new" and this moves it to "reviewing") — if so,
      // remove it locally instead of leaving a stale, wrongly-labeled card
      // sitting in the list until some unrelated reload happens to catch it.
      const f = assignmentFiltersRef.current;
      const stillMatchesFilter = f.status_filter
        ? status === f.status_filter
        : !(f.hide_archived && status === "archived");

      setAssignments((prev) => {
        if (!prev) return prev;
        if (!stillMatchesFilter) {
          return {
            ...prev,
            items: prev.items.filter((a) => a.assignment_id !== assignmentId),
            total_count: Math.max(0, prev.total_count - 1),
          };
        }
        return {
          ...prev,
          items: prev.items.map((a) =>
            a.assignment_id === assignmentId ? { ...a, status } : a,
          ),
        };
      });
      // Update selectedAssignment if open
      setSelectedAssignment((prev) =>
        prev?.assignment_id === assignmentId ? { ...prev, status } : prev,
      );
      await loadStatusCounts();
    },
    [isAdmin, loadStatusCounts],
  );

  // Tech tags modal
  const [editingTechManager, setEditingTechManager] = useState<TheirStackTechManager | null>(null);
  const openTechTagsModal = useCallback((tm: TheirStackTechManager) => setEditingTechManager(tm), []);
  const closeTechTagsModal = useCallback(() => setEditingTechManager(null), []);

  const handleUpdateTechTags = useCallback(async (employeeCode: string, tags: string[]) => {
    const result = await updateTechTags(employeeCode, tags);
    setTechManagers((prev) =>
      prev.map((tm) =>
        tm.employee_code === employeeCode ? { ...tm, tech_tags: result.tech_tags } : tm,
      ),
    );
    setEditingTechManager((prev) =>
      prev?.employee_code === employeeCode ? { ...prev, tech_tags: result.tech_tags } : prev,
    );
  }, []);

  const value: TheirStackJobsState = {
    isAdmin,
    jobs,
    jobsLoading,
    jobFilters,
    setJobFilters,
    loadJobs,
    assignments,
    assignmentsLoading,
    assignmentFilters,
    setAssignmentFilters,
    loadAssignments,
    statusCounts,
    loadStatusCounts,
    techManagers,
    techManagersLoaded,
    loadTechManagers,
    selectedJob,
    openJob,
    closeJob,
    handleActivateJob,
    handleAssignJob,
    handleUnassignJob,
    handleReassignAssignment,
    selectedAssignment,
    assignmentDetailLoading,
    openAssignment,
    closeAssignment,
    updateAssignmentEnhancement,
    assignmentHistory,
    historyLoading,
    loadAssignmentHistory,
    handleStatusUpdate,
    editingTechManager,
    openTechTagsModal,
    closeTechTagsModal,
    handleUpdateTechTags,
  };

  return (
    <TheirStackJobsContext.Provider value={value}>
      {children}
    </TheirStackJobsContext.Provider>
  );
}
